import { PageviewData, useUmami } from '@danielgtmn/umami-react';
import { useMatomo } from '@jonkoops/matomo-tracker-react';
import { useEventCallback } from '@mui/material';
import { FeatureFlags } from 'application/constants/env-variables';
import { useAuth } from 'helpers/auth/auth-interceptor.ts';
import { getCurrentSupportedLanguage } from 'application/localization/localization.ts';
import { useTranslation } from 'react-i18next';

type EventData = {
  category: string;
  action: string;
  name?: string;
  value?: number;
  customData?: Record<string, any>;
} & PageviewData &
  Record<string, any>;

export function useAnalytics() {
  const { trackEvent: trackMatomoEvent } = useMatomo();
  const { trackPageview: trackPageviewUmami, identify } = useUmami();
  const authData = useAuth();
  const i18n = useTranslation();

  const trackEvent = useEventCallback((data: EventData) => {
    trackMatomoEvent(data);
    const userId = authData === 'loading' ? '' : authData?.claims.id ?? '';
    const roles = authData === 'loading' ? [] : authData?.claims.roles ?? [];
    const language = getCurrentSupportedLanguage(i18n.i18n);

    trackUmami({
      ...data,
      customData: {
        ...data.customData,
        userId,
        roles,
        language,
      },
    });
  });

  const trackPageView = useEventCallback((data: EventData) => {
    trackPageviewUmami(data);
  });

  const identifyUser = useEventCallback(
    (userId: string, userData?: Record<string, any>) => {
      if (!userId) return;
      identify(userId, userData);
    },
  );

  return { trackEvent, trackPageView, identifyUser };
}

/*
 * function that could be used if you can't use hooks for some reason.
 * Copied from https://github.com/danielgtmn/react-umami/blob/main/src/index.tsx
 */
export function trackUmami(pageviewData?: EventData) {
  if (typeof window !== 'undefined' && (window as any).umami) {
    if (typeof (window as any).umami.track === 'function') {
      if (pageviewData && Object.keys(pageviewData).length > 0) {
        // For pageviews with custom data, call track with the data
        const data = {
          ...processUmamiEventData(pageviewData),
          url:
            pageviewData.url ||
            window.location.pathname + window.location.search,
          title: pageviewData.title || document.title,
          referrer: pageviewData.referrer || document.referrer,
        };

        (window as any).umami.track(data);
      } else {
        // For default pageviews, call track without arguments (defaults to current page)
        (window as any).umami.track();
      }
    }
  }
}
function processUmamiEventData(data: EventData) {
  const { name, category, action, customData, ...rest } = data;
  const additionalData: Record<string, any> = {};
  if (name) {
    additionalData['name'] = name;
  }

  return {
    name: category + ' - ' + action, //+ (name !== action ? ' - ' + name : '') too long (50 chars limit in umami) :(,
    website: FeatureFlags.umamiSiteId(),
    id: customData?.userId ?? undefined,
    data:
      Object.keys(additionalData).length > 0
        ? { ...additionalData, ...customData }
        : customData,
    ...rest,
  };
}
